import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowsAltOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import type { ApexOptions } from 'apexcharts';
import Chart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux';

import {
  FinancialInstrument,
  HISTORICAL_SCENARIO,
  retrieveHistory,
  selectors,
} from 'state';
import {
  fetchOptionIndicatorHistory,
  type OptionIndicatorHistory,
  type PriceGraphPeriod,
} from './optionIndicatorHistoryApi';

type GraphMode = 'price' | 'implied_volatility' | 'basis_points';

type Props = {
  instrument?: FinancialInstrument;
  animateHistoricalPriceUpdates?: boolean;
};

const periods: { value: PriceGraphPeriod; name: string }[] = [
  { value: 'one_day', name: '1D' },
  { value: 'one_month', name: '1M' },
  { value: 'six_months', name: '6M' },
  { value: 'one_year', name: '1Y' },
  { value: 'three_years', name: '3Y' },
];

const compactPrice = (value?: number) =>
  value == null || !Number.isFinite(value)
    ? '—'
    : value.toLocaleString(undefined, { maximumFractionDigits: 4 });

const numeric = (value: number | string | null | undefined) => {
  if (value == null) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const sameTimestamp = (left: string, right: string) => {
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime))
    return leftTime === rightTime;
  return left === right;
};

const graphModeFor = (
  resourcetype?: string,
  underlyingKind?: 'EQUITY' | 'BOND',
): GraphMode => {
  if (resourcetype !== 'SimplifiedOption') return 'price';
  if (underlyingKind === 'EQUITY') return 'implied_volatility';
  if (underlyingKind === 'BOND') return 'basis_points';
  return 'price';
};

const formatValue = (mode: GraphMode, value?: number) => {
  if (value == null || !Number.isFinite(value)) return '—';
  if (mode === 'implied_volatility')
    return `${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  if (mode === 'basis_points')
    return `${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} bp`;
  return compactPrice(value);
};

const PriceGraph: React.FC<Props> = ({
  instrument,
  animateHistoricalPriceUpdates = false,
}) => {
  const dispatch = useDispatch();
  const assets = useSelector(selectors.underlyingAssets);
  const session = useSelector(selectors.activeSession);
  const [period, setPeriod] = useState<PriceGraphPeriod>('one_day');
  const [expanded, setExpanded] = useState(false);
  const [indicatorHistory, setIndicatorHistory] = useState<{
    key: string;
    loading: boolean;
    error: boolean;
    result?: OptionIndicatorHistory;
  }>();
  const requestSequence = useRef(0);
  const previousInstrumentId = useRef<number>();

  const asset = instrument ? assets[instrument.underlying_asset] : undefined;
  const history = asset?.market_history?.[period];
  const isHistorical = session.scenario_type === HISTORICAL_SCENARIO;
  const graphMode = graphModeFor(asset?.resourcetype, asset?.underlying_kind);
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const isV5HistoricalPriceGraph =
    animateHistoricalPriceUpdates && isHistorical && graphMode === 'price';
  const isSameInstrumentUpdate =
    instrument?.id !== undefined &&
    previousInstrumentId.current === instrument.id;
  const animatePriceHistory =
    isV5HistoricalPriceGraph && isSameInstrumentUpdate && !prefersReducedMotion;
  const pendingChartTimestamp = asset?.pending_historical_chart_timestamp;
  const displayedHistory =
    isV5HistoricalPriceGraph && pendingChartTimestamp
      ? history?.filter(
          point => !sameTimestamp(point.timestamp, pendingChartTimestamp),
        )
      : history;
  const activeQuoteId = instrument?.simplified_option?.active_quote_id;
  const indicatorKey = `${asset?.id || ''}:${session.id}:${period}:${
    session.scenario_date || ''
  }:${activeQuoteId || ''}:${graphMode}`;

  useEffect(() => {
    previousInstrumentId.current = instrument?.id;
  }, [instrument?.id]);

  useEffect(() => {
    if (!asset || graphMode !== 'price' || history !== undefined) return;
    dispatch(
      retrieveHistory({
        underlying_asset_id: asset.id,
        period,
      }),
    );
  }, [asset, dispatch, graphMode, history, period]);

  useEffect(() => {
    if (!asset || graphMode === 'price') {
      requestSequence.current += 1;
      return;
    }
    const sequence = ++requestSequence.current;
    setIndicatorHistory({
      key: indicatorKey,
      loading: true,
      error: false,
    });
    fetchOptionIndicatorHistory({
      underlyingAssetId: asset.id,
      sessionId: session.id,
      period,
    })
      .then(result => {
        if (requestSequence.current !== sequence) return;
        setIndicatorHistory({
          key: indicatorKey,
          loading: false,
          error: false,
          result,
        });
      })
      .catch(() => {
        if (requestSequence.current !== sequence) return;
        setIndicatorHistory({
          key: indicatorKey,
          loading: false,
          error: true,
        });
      });
    return () => {
      if (requestSequence.current === sequence) requestSequence.current += 1;
    };
  }, [asset, graphMode, indicatorKey, period, session.id]);

  const activeIndicatorHistory =
    indicatorHistory?.key === indicatorKey ? indicatorHistory : undefined;

  const data = useMemo(() => {
    if (graphMode !== 'price') {
      return (activeIndicatorHistory?.result?.points || [])
        .map(point => ({
          x: point.timestamp,
          y:
            graphMode === 'implied_volatility'
              ? Number(point.value) * 100
              : Number(point.value),
        }))
        .filter(point => Number.isFinite(point.y));
    }
    return (displayedHistory || []).map(item => ({
      x: item.timestamp,
      y:
        asset?.resourcetype === 'FxForwards'
          ? (item.fwd_ask ?? item.ask ?? item.close)
          : (item.ask ?? item.close),
    }));
  }, [
    activeIndicatorHistory?.result?.points,
    asset?.resourcetype,
    graphMode,
    displayedHistory,
  ]);

  const currentValue = (() => {
    if (graphMode === 'implied_volatility') {
      const value = numeric(
        instrument?.simplified_option?.current_annualized_implied_volatility,
      );
      return value == null ? data.at(-1)?.y : value * 100;
    }
    if (graphMode === 'basis_points') {
      return (
        numeric(instrument?.simplified_option?.current_basis_points) ??
        data.at(-1)?.y
      );
    }
    return data.at(-1)?.y ?? asset?.ask;
  })();
  const firstValue = data[0]?.y;
  const valueMove = (() => {
    if (firstValue == null || currentValue == null) return undefined;
    if (graphMode === 'price')
      return firstValue === 0
        ? undefined
        : ((currentValue - firstValue) / firstValue) * 100;
    return currentValue - firstValue;
  })();

  const graphLabel =
    graphMode === 'implied_volatility'
      ? 'Implied volatility'
      : graphMode === 'basis_points'
        ? 'Basis points'
        : 'Price history';
  const historyLabel =
    graphMode === 'price'
      ? 'price history'
      : `${graphLabel.toLowerCase()} history`;
  const accessibleGraphLabel =
    graphMode === 'implied_volatility'
      ? 'implied volatility'
      : graphMode === 'basis_points'
        ? 'basis points'
        : 'price';
  const moveSuffix =
    graphMode === 'implied_volatility'
      ? ' pp'
      : graphMode === 'basis_points'
        ? ' bp'
        : '%';
  const loading =
    graphMode === 'price'
      ? history === undefined
      : !activeIndicatorHistory || activeIndicatorHistory.loading;
  const error = graphMode !== 'price' && activeIndicatorHistory?.error;

  const tooltipDateFormat = (() => {
    if (isHistorical) return 'ddd, d MMMM';
    if (period === 'one_day') return 'HH:mm';
    if (period === 'five_days') return 'ddd, d MMMM HH:mm';
    if (period === 'one_month' || period === 'six_months') return 'ddd, d MMMM';
    return 'd MMMM yyyy';
  })();

  const chartOptions = (full: boolean): ApexOptions => ({
    chart: {
      animations: animatePriceHistory
        ? {
            enabled: true,
            easing: 'easeinout',
            speed: 0,
            animateGradually: { enabled: false, delay: 0 },
            dynamicAnimation: { enabled: true, speed: 450 },
          }
        : { enabled: false },
      background: 'transparent',
      fontFamily: 'inherit',
      sparkline: { enabled: !full },
      toolbar: { show: full },
      zoom: { enabled: full },
    },
    colors: ['#2f7df4'],
    dataLabels: { enabled: false },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.4,
        opacityFrom: 0.42,
        opacityTo: 0.02,
        stops: [0, 92, 100],
      },
    },
    grid: {
      show: full,
      borderColor: '#e5ebf3',
      padding: full
        ? { top: 8, right: 12, bottom: 0, left: 6 }
        : { top: 4, right: 1, bottom: 1, left: 1 },
    },
    markers: { size: data.length === 1 ? 3 : 0, hover: { sizeOffset: 4 } },
    noData: {
      text: error
        ? `Unable to load ${graphLabel.toLowerCase()}`
        : loading
          ? `Loading ${graphLabel.toLowerCase()}…`
          : `No ${historyLabel}`,
      align: 'center',
      verticalAlign: 'middle',
      style: { color: '#718096', fontSize: '11px' },
    },
    stroke: {
      curve: graphMode === 'price' ? 'smooth' : 'stepline',
      width: full ? 3 : 2,
    },
    tooltip: {
      theme: 'light',
      x: { show: !isHistorical, format: tooltipDateFormat },
      y: { formatter: value => formatValue(graphMode, value) },
    },
    xaxis: {
      type: 'datetime',
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { show: full && !isHistorical },
      tooltip: { enabled: false },
    },
    yaxis: {
      show: full,
      labels: { formatter: value => formatValue(graphMode, value) },
    },
  });

  if (!instrument || !asset) {
    return (
      <section
        className="markets-v4-price-graph is-empty"
        aria-label="Price chart"
      >
        <span className="markets-v4-price-graph__eyebrow">Price chart</span>
        <strong>Select an instrument</strong>
        <p>Price history will appear here.</p>
      </section>
    );
  }

  const chart = (full: boolean) => (
    <Chart
      options={chartOptions(full)}
      series={[{ name: graphLabel, data }]}
      type="area"
      height={full ? 460 : '100%'}
    />
  );

  return (
    <>
      <section
        className="markets-v4-price-graph"
        aria-label={`${asset.ticker} ${accessibleGraphLabel} chart`}
      >
        <header className="markets-v4-price-graph__head">
          <div className="markets-v4-price-graph__identity">
            <span className="markets-v4-price-graph__eyebrow">
              {graphLabel}
            </span>
            <div>
              <strong>{asset.ticker}</strong>
              <span>{formatValue(graphMode, currentValue)}</span>
              {valueMove !== undefined && (
                <small
                  className={valueMove < 0 ? 'is-negative' : 'is-positive'}
                >
                  {valueMove >= 0 ? '+' : ''}
                  {valueMove.toFixed(2)}
                  {moveSuffix}
                </small>
              )}
            </div>
          </div>

          <div className="markets-v4-price-graph__controls">
            {isHistorical ? (
              <span className="markets-v4-price-graph__historical">
                Historical
              </span>
            ) : (
              <div
                className="markets-v4-price-graph__periods"
                aria-label="Chart period"
              >
                {periods.map(item => (
                  <button
                    key={item.value}
                    type="button"
                    className={period === item.value ? 'is-active' : ''}
                    aria-pressed={period === item.value}
                    onClick={() => setPeriod(item.value)}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            )}
            <Button
              type="text"
              size="small"
              icon={<ArrowsAltOutlined />}
              aria-label={`Expand ${accessibleGraphLabel} chart`}
              onClick={() => setExpanded(true)}
            />
          </div>
        </header>

        <div className="markets-v4-price-graph__canvas">{chart(false)}</div>
      </section>

      <Modal
        className="markets-v4-price-graph-modal"
        width={960}
        open={expanded}
        footer={null}
        onCancel={() => setExpanded(false)}
        title={`${asset.ticker} ${graphLabel.toLowerCase()}`}
        destroyOnHidden
      >
        <div className="markets-v4-price-graph-modal__periods">
          {!isHistorical &&
            periods.map(item => (
              <button
                key={item.value}
                type="button"
                className={period === item.value ? 'is-active' : ''}
                aria-pressed={period === item.value}
                onClick={() => setPeriod(item.value)}
              >
                {item.name}
              </button>
            ))}
        </div>
        {chart(true)}
      </Modal>
    </>
  );
};

export default PriceGraph;
